/**
 * Scope-of-Work workbook — the per-property construction budget.
 *
 * Contract: budget-scope-of-work-draws.md §3. GET returns `scopes[]` (one sheet
 * per subject property); each sheet has editable `lineItems[]`, model-computed
 * read-only fields, a per-line `reimbursed` flag, sheet-level footer totals, and
 * `populatedFrom` provenance.
 *
 * FINAL budget (2026-09-22): each sheet carries the reviewer's `budgetSource`
 * (which budget it is from — picked in BudgetSourcePicker) and the
 * `budgetLocked` lock. A locked sheet is the budget of record servicing and
 * TrustPoint read, so it is read-only here until someone unlocks it. Editing sends the WHOLE sheet back via PUT (rows
 * keep their oid/_id) — there is no per-cell PATCH, which is why edits batch into
 * a local draft behind an explicit Save (rationale §2d).
 *
 * ── Why this is a hand-built table and not AG Grid ──────────────────────────
 * CLAUDE.md's hard default is AG Grid for any multi-row/multi-column grid of
 * records, and the draws ledger below still uses it. This one is the documented
 * exception: it is a *form* rendered in a grid, not a data table. Every cell is a
 * live input (no click-to-edit state), rows expand into a full-width detail form
 * with cross-check readouts, and the row tint/flag recompute per keystroke. AG
 * Grid's editing model is cell-at-a-time with an editor lifecycle, and its
 * master/detail is an enterprise row-expansion feature aimed at read-only nested
 * grids — bending either into an always-on spreadsheet-style form costs more
 * complexity than the table markup it would replace. The draws ledger, which IS
 * a read-oriented record grid, stays on AG Grid per the convention.
 * ────────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import LockOpenRounded from "@mui/icons-material/LockOpenRounded";
import LockRounded from "@mui/icons-material/LockRounded";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import ConfirmationDialog from "../../../../components/ui/ConfirmationDialog";
import BudgetSourcePicker, { sourceSummary } from "./BudgetSourcePicker";
import { T, cardSx, labelSx, money, num, pct } from "./budgetDrawsTheme";
import SowLineRow, { lineMath } from "./SowLineRow";
import RebuildSheetDialog from "./RebuildSheetDialog";
import { ReportPhotoLightbox } from "./ReportPhotoStrip";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

/**
 * Fixed widths for every column EXCEPT Line item, which is left unsized so it
 * absorbs whatever the card has left over. Keep `total` in step if you add or
 * resize one — it's what the table's minWidth is derived from.
 */
const COL_W = {
  category: 148,
  costType: 128,
  est: 118,
  pctComplete: 118,
  spent: 118,
  pctSpent: 128,
  flag: 104,
};
const COL_W_TOTAL = Object.values(COL_W).reduce((a, w) => a + w, 0);

/**
 * All of the table's presentation, kept in one readable block.
 *
 * Everything reads at one size: `T.cellFont` for data cells (item name, both
 * chip selects, numeric inputs, computed cells, the reimburse flag, the
 * detail-row fields) and the same size for the column headers, which are
 * distinguished by uppercase + letter-spacing rather than by scale.
 */
const tableSx = {
  overflowX: "auto",
  // `table-layout: fixed` makes every column honor its declared width exactly,
  // which hands ALL the leftover width to the one column with none — Line item.
  // The table then consumes the card edge to edge instead of leaving a gutter,
  // and the ellipsis on a truncated name lands at a predictable position.
  "& table": {
    borderCollapse: "collapse",
    tableLayout: "fixed",
    width: "100%",
    minWidth: COL_W_TOTAL + 220, // 220 = narrowest Line item before scrolling
  },

  "& thead th": {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: T.faint,
    textAlign: "left",
    padding: "9px 14px",
    borderBottom: `1px solid ${T.line}`,
    background: T.subtle,
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  "& thead th.num, & td.num": { textAlign: "right" },
  "& thead th.computed": { background: T.computed },

  "& tbody td": {
    padding: 0,
    borderBottom: `1px solid ${T.line2}`,
    // Centered: a row whose item name wraps to two or three lines grows taller,
    // and its numbers should sit in the middle of that row rather than clinging
    // to the top edge with dead space beneath them.
    verticalAlign: "middle",
    fontSize: T.cellFont,
  },
  "& tbody tr.item-row": { transition: "background .1s" },
  "& tbody tr.item-row:hover": { background: "#FAFCFD" },
  "& tbody tr.item-row.reimbursed": { background: T.greenTint },
  "& tbody tr.item-row.reimbursed:hover": { background: "#E4F3EA" },
  // A row a photo chip pointed at: held tinted long enough to find by eye after
  // the scroll, then faded out. Beats .reimbursed so the highlight always shows.
  "& tbody tr.item-row.flash, & tbody tr.item-row.flash:hover": {
    background: T.accentTint2,
    animation: "sowFlash 2.2s ease-out forwards",
  },
  "@keyframes sowFlash": {
    "0%,55%": { background: T.accentTint2 },
    "100%": { background: "transparent" },
  },
  // The per-line camera — only on lines a report photo actually evidences.
  "& .line-cam": {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    flexShrink: 0,
    border: `1px solid ${T.accentTint2}`,
    background: T.accentTint,
    color: T.accentInk,
    borderRadius: "5px",
    padding: "1px 5px",
    font: "inherit",
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1.4,
    cursor: "pointer",
  },
  "& .line-cam:hover": { background: T.accentTint2 },
  "& td .cellpad": { padding: "9px 14px", fontSize: T.cellFont },
  "& td.computed": {
    background: T.computed,
    fontFamily: T.mono,
    fontSize: T.cellFont,
    color: T.ink2,
  },
  "& tr.reimbursed td.computed": { background: T.greenTint2 },

  // `minWidth: 0` is what lets the name shrink below its content width — without
  // it the flex item refuses to shrink and the ellipsis never appears.
  "& .item-cell": {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    minWidth: 0,
    paddingLeft: "12px",
  },
  "& .item-cell > *": { minWidth: 0 },
  "& .flag-cell": { paddingRight: "16px", textAlign: "right" },
  "& .chip-cell": { padding: "6px 10px", minWidth: 0, overflow: "hidden" },

  "& .disclose": {
    width: 20,
    height: 20,
    flex: "none",
    display: "grid",
    placeItems: "center",
    borderRadius: "5px",
    border: "none",
    background: "transparent",
    color: T.faint,
    cursor: "pointer",
    padding: 0,
    transition: "transform .15s",
  },
  "& .disclose:hover": { background: T.line2, color: T.ink },
  "& tr.open .disclose": { transform: "rotate(90deg)", color: T.accentInk },

  // Single line, ellipsis when it overruns. The column is wide (it takes the
  // table's leftover width), so truncation is the exception, not the rule.
  "& .item-name-in": {
    display: "block",
    border: "none",
    background: "transparent",
    width: "100%",
    fontWeight: 500,
    fontSize: T.cellFont,
    lineHeight: 1.45,
    padding: "9px 0",
    minWidth: 0,
    font: "inherit",
    fontFamily: "inherit",
    color: T.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .item-name-in:hover": { background: "#F2F5F7", borderRadius: "5px" },
  "& .item-name-in:focus": {
    background: "#fff",
    boxShadow: `inset 0 0 0 1.5px ${T.accent}`,
    borderRadius: "5px",
    outline: "none",
    paddingLeft: "6px",
  },

  // `display: block` drops the inline-baseline descender gap, so the number sits
  // on the row's true vertical center rather than a couple of px below it.
  "& .cell-in": {
    display: "block",
    border: "none",
    background: "transparent",
    width: "100%",
    padding: "9px 14px",
    fontFamily: T.mono,
    fontSize: T.cellFont,
    textAlign: "right",
    color: T.ink,
  },

  // Kill the native number spinners. They appear on hover, overlap the value and
  // visibly shift the digits — exactly the jitter a numeric column can't have.
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
  "& .cell-in:hover": { background: "#F2F5F7" },
  "& .cell-in:focus": {
    background: "#fff",
    boxShadow: `inset 0 0 0 1.5px ${T.accent}`,
    outline: "none",
  },

  "& select.chip-sel": {
    appearance: "none",
    border: "1px solid transparent",
    background: "transparent",
    fontSize: T.cellFont,
    fontWeight: 600,
    borderRadius: "99px",
    padding: "3px 9px",
    cursor: "pointer",
    color: T.muted,
    font: "inherit",
    fontFamily: "inherit",
    maxWidth: "100%",
  },
  "& select.chip-sel:hover": { borderColor: T.line, background: "#fff" },
  "& select.chip-sel.has-val": { background: "#EEF2F5", color: T.ink2 },
  "& select.chip-sel.hard": { background: "#EDF2FA", color: "#3B5A8A" },
  "& select.chip-sel.soft": { background: "#F4EFF9", color: "#6E4E9E" },

  "& .pct-cell": {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "8px 14px",
    minWidth: 96,
  },
  "& .pct-cell input": {
    border: "none",
    background: "transparent",
    fontFamily: T.mono,
    fontSize: T.cellFont,
    textAlign: "right",
    width: "100%",
    padding: "1px 0",
    color: T.ink,
  },
  "& .pct-cell input:hover": { background: "#F2F5F7" },
  "& .pct-cell input:focus": {
    background: "#fff",
    boxShadow: `0 0 0 1.5px ${T.accent}`,
    borderRadius: "3px",
    outline: "none",
  },
  "& .pct-track": {
    height: 3,
    borderRadius: "2px",
    background: "#E7EBEF",
    overflow: "hidden",
  },
  "& .pct-fill": {
    height: "100%",
    background: T.accent,
    borderRadius: "2px",
    transition: "width .2s",
  },

  "& .reimb-flag": {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    fontSize: T.cellFont,
    fontWeight: 600,
    color: T.green,
    whiteSpace: "nowrap",
  },
  "& .reimb-flag .d": {
    width: 6,
    height: 6,
    borderRadius: "99px",
    background: T.green,
  },

  // Detail row
  "& tr.detail td": {
    background: T.subtle,
    borderBottom: `1px solid ${T.line}`,
    padding: "16px 20px 18px 43px",
  },
  "& .detail-grid": {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      md: "minmax(260px,1.4fr) minmax(300px,1fr)",
    },
    gap: "24px",
  },
  "& .detail-grid label": {
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: T.faint,
    marginBottom: "5px",
  },
  "& .detail-grid textarea": {
    width: "100%",
    minHeight: 76,
    resize: "vertical",
    border: `1px solid ${T.line}`,
    borderRadius: T.radiusSm,
    padding: "9px 11px",
    fontSize: T.cellFont,
    lineHeight: 1.5,
    background: "#fff",
    font: "inherit",
    fontFamily: "inherit",
    color: T.ink,
  },
  "& .detail-grid textarea:focus": {
    borderColor: T.accent,
    outline: "none",
    boxShadow: "0 0 0 3px rgba(14,143,168,.12)",
  },
  "& .unit-math": {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px",
  },
  "& .unit-math .uf input": {
    width: "100%",
    border: `1px solid ${T.line}`,
    borderRadius: T.radiusSm,
    padding: "7px 10px",
    fontFamily: T.mono,
    fontSize: T.cellFont,
    background: "#fff",
    color: T.ink,
  },
  "& .unit-math .uf input:focus": {
    borderColor: T.accent,
    outline: "none",
    boxShadow: "0 0 0 3px rgba(14,143,168,.12)",
  },
  "& .crosscheck": {
    gridColumn: "1 / -1",
    fontSize: T.cellFont,
    fontFamily: T.mono,
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    paddingTop: "2px",
  },
  "& .row-actions": {
    gridColumn: "1 / -1",
    display: "flex",
    justifyContent: "flex-end",
  },
  "& .link-danger": {
    fontSize: T.cellFont,
    fontWeight: 600,
    color: T.red,
    padding: "4px 6px",
    borderRadius: "6px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    font: "inherit",
  },
  "& .link-danger:hover": { background: "#FBEEEC" },

  // A locked (FINAL) sheet is read-only: the inputs stay on screen as values,
  // but nothing takes a click or a keystroke, and the row delete is gone.
  "&.locked input, &.locked select, &.locked textarea": {
    pointerEvents: "none",
  },
  "&.locked .cell-in:hover, &.locked .item-name-in:hover, &.locked .pct-cell input:hover, &.locked select.chip-sel:hover":
    { background: "transparent", borderColor: "transparent" },
  "&.locked .link-danger": { display: "none" },
};

const sheetIdOf = (s) => String(s?.subjectPropertyId || s?._id || s?.oid || "");

/** Stable client keys so React doesn't remount rows as values change. */
let uidSeq = 0;
const withUids = (lineItems) =>
  (lineItems || []).map((li) => ({
    ...li,
    _uid: String(li?._id || li?.oid || `new-${(uidSeq += 1)}`),
  }));

const emptyRow = () => ({
  _uid: `new-${(uidSeq += 1)}`,
  _new: true,
  costCategory: "Hard Costs",
  itemName: "",
  itemCategory: null,
  description: "",
  quantityRequired: null,
  unitPrice: null,
  costPerSquareFoot: null,
  squareFootageRequired: null,
  estimatedTotalLineItemCost: null,
  percentComplete: 0,
  costSpentToDate: 0,
});

/** The pill that says where these rows came from (rationale §2a). */
const ProvenanceChip = ({ sheet }) => {
  const p = sheet?.populatedFrom;
  if (!p) return null;
  const label =
    p.docType ||
    (p.source === "feasibility_review" ? "Feasibility Report" : "Budget");
  const when = sheet.populatedAt ? formatDate(sheet.populatedAt, null) : null;
  // Tier 1 = third-party feasibility, tier 2 = the borrower's own budget. That
  // difference is how hard to scrutinize the numbers, so it goes in the tooltip.
  const tier =
    p.precedenceTier === 1
      ? "Third-party feasibility review (precedence tier 1)"
      : p.precedenceTier === 2
        ? "Borrower-submitted budget (precedence tier 2)"
        : null;
  const tip = [tier, p.blobName].filter(Boolean).join(" · ");

  return (
    <Tooltip title={tip || ""} placement="top">
      <Flx
        ac
        sx={{
          gap: 0.75,
          fontSize: 12,
          color: T.muted,
          bgcolor: "#F6F8FA",
          border: `1px solid ${T.line}`,
          borderRadius: "99px",
          padding: "3px 10px",
          cursor: "default",
        }}
      >
        <Box
          sx={{ width: 6, height: 6, borderRadius: "99px", bgcolor: T.accent }}
        />
        <Txt sx={{ fontSize: 12, color: T.muted }}>
          From{" "}
          <Box component="span" sx={{ color: T.ink2, fontWeight: 600 }}>
            {label}
          </Box>
          {when ? ` · ${when}` : ""}
        </Txt>
      </Flx>
    </Tooltip>
  );
};

const Total = ({ label, value }) => (
  <Flx column sx={{ alignItems: "flex-end", gap: 0.25 }}>
    <Txt sx={{ ...labelSx, fontSize: 12 }}>{label}</Txt>
    <Txt
      sx={{ fontFamily: T.mono, fontSize: 12, fontWeight: 600, color: T.ink }}
    >
      {value}
    </Txt>
  </Flx>
);

const ScopeOfWorkWorkbook = ({
  scopes,
  properties,
  loading,
  saving,
  onSave, // (subjectPropertyId, sheetBody) => Promise
  onRebuild, // (refresh) => Promise
  onRowsChange, // (rows) => void — lifts the live budget total to the capital band
  defaultCollapsed = false, // true once draws exist — see below
  // Report photos (budgets-draws-report-photos.md): a line any photo evidences
  // gets a camera with the count, opening that line's photos across every
  // report, oldest first. Keyed by ScopeOfWork.lineItems[].oid.
  photoLineMap,
  focusLineId, // a line to scroll to + flash, set by a photo / evidence chip
  onFocusHandled,
  onPreviewReport,
  api,
  dealId,
  // FINAL budget: (subjectPropertyId, locked) and
  // (subjectPropertyId, source, { rebuild }) — both owned by the tab.
  onLock,
  onSetSource,
}) => {
  const [activeSheetId, setActiveSheetId] = useState("");
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [openRows, setOpenRows] = useState(() => new Set());
  const [confirmRebuild, setConfirmRebuild] = useState(false);
  // Once the loan is drawing, the budget is reference material rather than the
  // thing being worked — it folds away to put the ledger on screen, and the
  // header keeps a one-line summary so nothing is hidden without a trace.
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const scrollRef = useRef(null);
  // The line whose photos are open in the lightbox, and the transient flash on
  // the row a photo chip pointed at.
  const [photoLineId, setPhotoLineId] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [flashId, setFlashId] = useState(null);
  // "pick" (Change source) · "browse" (Source budgets) · null
  const [pickerMode, setPickerMode] = useState(null);
  const [confirmLock, setConfirmLock] = useState(null); // "lock" | "unlock" | null

  const activeSheet = useMemo(
    () =>
      (scopes || []).find((s) => sheetIdOf(s) === String(activeSheetId)) ||
      scopes?.[0] ||
      null,
    [scopes, activeSheetId],
  );

  // Re-seed the draft whenever the server hands us a different sheet (property
  // switch, refetch after save/rebuild). Keyed on identity + row count so a save
  // response replaces the draft instead of leaving it stale.
  const seedKey = `${sheetIdOf(activeSheet)}:${activeSheet?.lineItems?.length ?? 0}:${activeSheet?.updateDateTime ?? ""}`;
  useEffect(() => {
    setRows(withUids(activeSheet?.lineItems));
    setDirty(false);
    setOpenRows(new Set());
  }, [seedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onRowsChange?.(rows);
  }, [rows, onRowsChange]);

  /**
   * "Clicking a chip scrolls to and highlights that row in the budget grid"
   * (budgets-draws-report-photos.md). The line may be on a sheet that isn't
   * active and the whole workbook may be folded away once draws exist, so this
   * switches sheet and unfolds before scrolling — otherwise the click looks like
   * it did nothing.
   */
  useEffect(() => {
    if (!focusLineId) return undefined;
    const onActive = rows.some(
      (r) => String(r?._id || r?.oid || "") === focusLineId,
    );
    if (!onActive) {
      const target = (scopes || []).find((s) =>
        (s?.lineItems || []).some(
          (li) => String(li?._id || li?.oid || "") === focusLineId,
        ),
      );
      if (target) {
        setActiveSheetId(sheetIdOf(target));
        return undefined; // re-runs once the new sheet's rows are seeded
      }
    }
    setCollapsed(false);
    setFlashId(focusLineId);
    // One frame so the unfolded table has laid out before we measure it.
    const raf = requestAnimationFrame(() => {
      const el = scrollRef.current?.querySelector(
        `[data-line-id="${focusLineId}"]`,
      );
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const clear = setTimeout(() => setFlashId(null), 2200);
    onFocusHandled?.();
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(clear);
    };
  }, [focusLineId, rows, scopes, onFocusHandled]);

  /** The line's photos across every report, oldest first. */
  const photoItems = useMemo(
    () => (photoLineId ? photoLineMap?.get(photoLineId) || [] : []),
    [photoLineId, photoLineMap],
  );

  const openLinePhotos = useCallback((lineId) => {
    setPhotoLineId(lineId);
    setPhotoIdx(0);
  }, []);

  const locked = !!activeSheet?.budgetLocked;

  const handleChange = useCallback((uid, field, value) => {
    if (locked) return; // a FINAL sheet takes no edits until it is unlocked
    setRows((prev) =>
      prev.map((r) => (r._uid === uid ? { ...r, [field]: value } : r)),
    );
    setDirty(true);
  }, [locked]);

  const handleToggle = useCallback((uid) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  }, []);

  const handleDelete = useCallback((uid) => {
    if (locked) return;
    setRows((prev) => prev.filter((r) => r._uid !== uid));
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.delete(uid);
      return next;
    });
    setDirty(true);
  }, [locked]);

  const handleAddRow = useCallback(() => {
    const row = emptyRow();
    setRows((prev) => [...prev, row]);
    setOpenRows((prev) => new Set(prev).add(row._uid));
    setDirty(true);
    // New row lands at the bottom of a long sheet — take the operator there.
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!activeSheet) return;
    const lineItems = rows.map((r) => {
      // Strip client-only keys and the response-only convenience flag; keep
      // oid/_id so the backend preserves row identity.
      const { _uid, _new, reimbursed, ...rest } = r;
      return rest;
    });
    const body = { ...activeSheet, lineItems };
    delete body.budgetTabEligible; // response-only
    await onSave(activeSheet.subjectPropertyId, body);
    setDirty(false);
  }, [activeSheet, rows, onSave]);

  const handleRebuildClick = useCallback(() => {
    // Nothing to lose on an empty sheet — skip straight to the rebuild.
    if (rows.length === 0) onRebuild(false);
    else setConfirmRebuild(true);
  }, [rows.length, onRebuild]);

  const handleRebuildConfirm = useCallback(async () => {
    setConfirmRebuild(false);
    await onRebuild(true);
  }, [onRebuild]);

  const addressFor = useCallback(
    (subjectPropertyId) => {
      const p = (properties || []).find(
        (x) => String(x?._id || x?.oid) === String(subjectPropertyId),
      );
      return (
        addressLabel(p?.address) ||
        addressLabel(p?.propertyAddress) ||
        "Property"
      );
    },
    [properties],
  );

  // Footer totals. The backend serializes these on the sheet; while a draft is
  // dirty they're recomputed locally so editing previews live. Same formulas as
  // ScopeOfWork's computed_fields (weighted by line cost, not a naive mean).
  const totals = useMemo(() => {
    const budget = rows.reduce(
      (a, r) => a + num(r.estimatedTotalLineItemCost),
      0,
    );
    if (!dirty && activeSheet) {
      return {
        budget,
        spent: num(activeSheet.totalCostSpentToDate),
        pctSpent: num(activeSheet.totalPercentOfCostSpentToDate),
        weighted: num(activeSheet.totalWeightedAveragePercentComplete),
      };
    }
    const spent = rows.reduce((a, r) => a + num(r.costSpentToDate), 0);
    const weightedSum = rows.reduce(
      (a, r) => a + num(r.percentComplete) * num(r.estimatedTotalLineItemCost),
      0,
    );
    return {
      budget,
      spent,
      pctSpent: budget > 0 ? spent / budget : 0,
      weighted: budget > 0 ? weightedSum / budget : 0,
    };
  }, [rows, dirty, activeSheet]);

  // The reimburse gutter earns its width only when something is reimbursable.
  const showFlagColumn = useMemo(
    () => rows.some((r) => lineMath(r).reimbursed),
    [rows],
  );

  // No sheet on the loan yet. This is a STATE OF THE CARD, not a different
  // card: the header (chevron, "Scope of Work", and the section anchor the
  // Budget & Draws jump list scrolls to) still renders, and the build prompt
  // goes in the body where the table would be. Returning a bare centred box
  // here instead made the section vanish from the page — and with it its
  // heading — on exactly the loans that need building.
  const isEmpty = !scopes || scopes.length === 0;

  return (
    <Box sx={cardSx}>
      {/* Header: title · property · provenance · actions */}
      <Flx
        jb
        ac
        wrap
        sx={{ gap: 1.5, p: "16px 20px", borderBottom: `1px solid ${T.line2}` }}
      >
        <Flx ac wrap sx={{ gap: 1.5, minWidth: 0 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={
              collapsed ? "Expand scope of work" : "Collapse scope of work"
            }
            sx={{
              width: 26,
              height: 26,
              flex: "none",
              display: "grid",
              placeItems: "center",
              borderRadius: "7px",
              border: "none",
              background: "transparent",
              color: T.muted,
              cursor: "pointer",
              p: 0,
              transition: "transform .18s",
              transform: collapsed ? "rotate(-90deg)" : "none",
              "&:hover": { background: T.line2, color: T.ink },
            }}
          >
            <KeyboardArrowDownRounded sx={{ fontSize: 18 }} />
          </Box>
          <Txt
            sx={{
              fontSize: 15.5,
              fontWeight: 700,
              color: T.ink,
              letterSpacing: "-0.01em",
            }}
          >
            Scope of Work
          </Txt>
          {isEmpty && (
            <Txt sx={{ fontSize: 12, color: T.muted }}>No sheet yet</Txt>
          )}
          {collapsed && !isEmpty && (
            <Txt sx={{ fontSize: 12, color: T.muted }}>
              ·{" "}
              <Box
                component="span"
                sx={{ color: T.ink2, fontFamily: T.mono, fontWeight: 600 }}
              >
                {rows.length}
              </Box>{" "}
              line items ·{" "}
              <Box
                component="span"
                sx={{ color: T.ink2, fontFamily: T.mono, fontWeight: 600 }}
              >
                {money(totals.budget)}
              </Box>{" "}
              budget ·{" "}
              <Box
                component="span"
                sx={{ color: T.ink2, fontFamily: T.mono, fontWeight: 600 }}
              >
                {pct(totals.weighted, 1)}
              </Box>{" "}
              complete
            </Txt>
          )}
          {!isEmpty &&
            (!collapsed && scopes.length > 1 ? (
              <TextField
                select
                size="small"
                value={sheetIdOf(activeSheet)}
                onChange={(e) => setActiveSheetId(e.target.value)}
                InputProps={{ sx: { fontSize: 12 } }}
                sx={{ minWidth: 220 }}
              >
                {scopes.map((s) => (
                  <MenuItem
                    key={sheetIdOf(s)}
                    value={sheetIdOf(s)}
                    sx={{ fontSize: 12, gap: 0.75 }}
                  >
                    {/* The padlock per property: which sheets are FINAL. */}
                    {s.budgetLocked ? (
                      <LockRounded sx={{ fontSize: 14, color: T.green }} />
                    ) : (
                      <LockOpenRounded sx={{ fontSize: 14, color: T.faint }} />
                    )}
                    {addressFor(s.subjectPropertyId)}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Txt sx={{ fontSize: 12, color: T.muted }}>
                {addressFor(activeSheet?.subjectPropertyId)}
              </Txt>
            ))}
          {!isEmpty && <ProvenanceChip sheet={activeSheet} />}
          {!isEmpty && locked && (
            <Tooltip
              title={[
                activeSheet?.budgetLockedBy ? `Locked by ${activeSheet.budgetLockedBy}` : null,
                activeSheet?.budgetLockedAt
                  ? formatDateTime(activeSheet.budgetLockedAt, "datetime")
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
              placement="top"
            >
              <Flx
                ac
                sx={{
                  gap: 0.5,
                  px: 1,
                  py: "3px",
                  borderRadius: "99px",
                  bgcolor: T.greenTint,
                  border: `1px solid ${T.greenLine}`,
                }}
              >
                <LockRounded sx={{ fontSize: 13, color: T.green }} />
                <Txt sx={{ fontSize: 12, fontWeight: 700, color: T.green }}>
                  Final budget
                </Txt>
              </Flx>
            </Tooltip>
          )}
        </Flx>

        {/* Editing actions belong to the open sheet — collapsed, they'd act on
            something the operator can't see.
            NOTE: unmount rather than `display: none` via sx. Flx spreads `sx`
            over its own `display: "flex"`, so passing a display key here (even
            `undefined`) drops the flex layout and with it the `gap`. */}
        {/* With no sheet there is nothing to rebuild, add a line to or save —
            the one action that applies is the build prompt in the body. */}
        {!collapsed && !isEmpty && locked && onLock && (
          <Flx ac wrap sx={{ gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LockOpenRounded sx={{ fontSize: 15 }} />}
              disabled={saving}
              onClick={() => setConfirmLock("unlock")}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              Unlock
            </Button>
          </Flx>
        )}
        {!collapsed && !isEmpty && !locked && (
          <Flx ac wrap sx={{ gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoAwesomeOutlined sx={{ fontSize: 15 }} />}
              disabled={loading || saving}
              onClick={handleRebuildClick}
              sx={{
                textTransform: "none",
                fontSize: 12,
                color: T.ink2,
                borderColor: T.line,
                "&:hover": { borderColor: "#D5DDE4", bgcolor: "#F6F8FA" },
              }}
            >
              Rebuild from documents
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddRounded sx={{ fontSize: 16 }} />}
              onClick={handleAddRow}
              sx={{
                textTransform: "none",
                fontSize: 12,
                color: T.ink2,
                borderColor: T.line,
                "&:hover": { borderColor: "#D5DDE4", bgcolor: "#F6F8FA" },
              }}
            >
              Add line
            </Button>
            <Button
              variant="contained"
              size="small"
              disableElevation
              disabled={!dirty || saving}
              onClick={handleSave}
              sx={{
                textTransform: "none",
                fontSize: 12,
                bgcolor: T.ink,
                "&:hover": { bgcolor: T.ink2 },
              }}
            >
              {saving ? "Saving…" : "Save sheet"}
            </Button>
            {onLock && (
              <Tooltip
                title={
                  dirty
                    ? "Save the sheet first"
                    : rows.length === 0
                      ? "A sheet with no line items cannot be locked"
                      : "Make this the budget of record — what servicing and TrustPoint read"
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    startIcon={<LockRounded sx={{ fontSize: 15 }} />}
                    disabled={dirty || saving || rows.length === 0}
                    onClick={() => setConfirmLock("lock")}
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    Lock as final budget
                  </Button>
                </span>
              </Tooltip>
            )}
          </Flx>
        )}
      </Flx>

      {/* Source line: which budget this sheet is from (the reviewer's pick),
          with Change source and the read-only list of every budget. */}
      {!collapsed && !isEmpty && onSetSource && (
        <Flx
          ac
          wrap
          sx={{
            gap: 1,
            p: "9px 20px",
            borderBottom: `1px solid ${T.line2}`,
            bgcolor: activeSheet?.budgetSource ? T.subtle : T.amberTint,
          }}
        >
          <Txt sx={{ fontSize: 12, color: activeSheet?.budgetSource ? T.muted : T.amberInk }}>
            {activeSheet?.budgetSource ? (
              <>
                Source:{" "}
                <Box component="span" sx={{ color: T.ink2, fontWeight: 600 }}>
                  {sourceSummary(activeSheet.budgetSource)}
                </Box>
                {activeSheet.budgetSource.selectedBy
                  ? ` · chosen by ${activeSheet.budgetSource.selectedBy}`
                  : ""}
                {activeSheet.budgetSource.selectedAt
                  ? ` ${formatDateTime(activeSheet.budgetSource.selectedAt, "date")}`
                  : ""}
              </>
            ) : (
              "No budget source chosen yet — pick which budget this sheet is from before locking it."
            )}
          </Txt>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={locked ? "Unlock the sheet to change its source" : ""}>
            <span>
              <Button
                size="small"
                variant="text"
                disabled={locked || saving}
                onClick={() => setPickerMode("pick")}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Change source
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="text"
            onClick={() => setPickerMode("browse")}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            Source budgets
          </Button>
        </Flx>
      )}

      {/* BODY, empty state: the build prompt sits where the table would be, so
          the card keeps its heading and its place on the page. */}
      {!collapsed && isEmpty && (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Txt sx={{ fontSize: 12, color: T.muted, display: "block", mb: 2 }}>
            No scope-of-work workbook yet. Build one from the loan&apos;s budget
            or feasibility documents.
          </Txt>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<AutoAwesomeOutlined sx={{ fontSize: 16 }} />}
            disabled={loading || saving}
            onClick={() => onRebuild(false)}
            sx={{
              textTransform: "none",
              bgcolor: T.ink,
              "&:hover": { bgcolor: T.ink2 },
            }}
          >
            Build from documents
          </Button>
        </Box>
      )}

      {!collapsed && !isEmpty && (
        <Box
          ref={scrollRef}
          className={locked ? "locked" : undefined}
          sx={{ ...tableSx, maxHeight: 620, overflowY: "auto" }}
        >
          <table>
            <thead>
              {/* Line item carries no width — under `table-layout: fixed` it takes
                everything the sized columns don't, so the table fills the card. */}
              <tr>
                <th>Line item</th>
                <th style={{ width: COL_W.category }}>Category</th>
                <th style={{ width: COL_W.costType }}>Cost type</th>
                <th className="num" style={{ width: COL_W.est }}>
                  Est. total
                </th>
                <th className="num" style={{ width: COL_W.pctComplete }}>
                  % complete
                </th>
                <th className="num" style={{ width: COL_W.spent }}>
                  Spent to date
                </th>
                <th
                  className="num computed"
                  style={{ width: COL_W.pctSpent }}
                  title="Computed by the server"
                >
                  % of cost spent
                </th>
                {showFlagColumn && (
                  <th
                    style={{ width: COL_W.flag }}
                    aria-label="Reimbursement status"
                  />
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const lineId = String(row?._id || row?.oid || "");
                return (
                  <SowLineRow
                    key={row._uid}
                    row={row}
                    lineId={lineId}
                    open={openRows.has(row._uid)}
                    showFlagColumn={showFlagColumn}
                    flash={flashId === lineId && lineId !== ""}
                    photoCount={
                      lineId ? photoLineMap?.get(lineId)?.length || 0 : 0
                    }
                    onOpenPhotos={openLinePhotos}
                    onChange={handleChange}
                    onToggle={handleToggle}
                    onDelete={handleDelete}
                  />
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={showFlagColumn ? 8 : 7}>
                    <Flx ac jc sx={{ py: 5 }}>
                      <Txt sx={{ fontSize: 12, color: T.faint }}>
                        This sheet has no line items yet.
                      </Txt>
                    </Flx>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      )}

      {/* Footer: legend + the four sheet totals. Four $0 / 0% totals under an
          empty card state nothing — it is dropped with the sheet. */}
      {!isEmpty && (
        <Flx
          jb
          ac
          wrap
          sx={{
            gap: 2,
            p: "13px 20px",
            borderTop: `1px solid ${T.line}`,
            bgcolor: T.subtle,
          }}
        >
          {/* Legend explains the row tint — pointless with the table hidden. The
            empty Box keeps `jb` pushing the totals to the right edge when it's
            gone. (Unmounted rather than hidden via sx — see the note above the
            head actions: an sx `display` key kills Flx's own flex layout.) */}
          {collapsed ? (
            <Box />
          ) : (
            <Flx ac sx={{ gap: 0.9 }}>
              {/* A dot, not a bordered square — a 12px rounded square with a
                border reads as an unchecked checkbox. This is the same dot the
                per-row Reimburse flag uses, so the legend keys to the thing it
                explains. */}
              <Box
                sx={{
                  flex: "none",
                  width: 7,
                  height: 7,
                  borderRadius: "99px",
                  bgcolor: T.green,
                }}
              />
              <Txt sx={{ fontSize: 12, color: T.muted }}>
                Green rows have costs already spent and are eligible for
                reimbursement in the next draw
              </Txt>
            </Flx>
          )}
          <Flx wrap sx={{ gap: 3.25 }}>
            <Total label="Budget total" value={money(totals.budget)} />
            <Total label="Spent to date" value={money(totals.spent)} />
            <Total label="% of cost spent" value={pct(totals.pctSpent, 2)} />
            <Total
              label="Weighted % complete"
              value={pct(totals.weighted, 2)}
            />
          </Flx>
        </Flx>
      )}

      {onSetSource && (
        <BudgetSourcePicker
          open={!!pickerMode}
          mode={pickerMode || "pick"}
          onClose={() => setPickerMode(null)}
          api={api}
          dealId={dealId}
          subjectPropertyId={
            activeSheet?.subjectPropertyId ? String(activeSheet.subjectPropertyId) : undefined
          }
          propertyLabel={addressFor(activeSheet?.subjectPropertyId)}
          current={activeSheet?.budgetSource}
          locked={locked}
          busy={saving}
          onConfirm={async (source, opts) => {
            await onSetSource(activeSheet?.subjectPropertyId, source, opts);
            setPickerMode(null);
          }}
        />
      )}

      <ConfirmationDialog
        open={!!confirmLock}
        title={confirmLock === "unlock" ? "Unlock the final budget?" : "Lock as the final budget?"}
        message={
          confirmLock === "unlock"
            ? "Unlocking re-enables editing and clears the lock. Until it is locked again, servicing and TrustPoint have no budget of record for this property."
            : `This sheet${activeSheet?.budgetSource ? ` (${sourceSummary(activeSheet.budgetSource)})` : ""} becomes the budget of record for ${addressFor(activeSheet?.subjectPropertyId)} — what servicing reads and what is pushed to TrustPoint. Line editing is disabled until it is unlocked.`
        }
        status={
          confirmLock === "lock" && !activeSheet?.budgetSource
            ? "No budget source has been chosen for this sheet."
            : null
        }
        confirmText={confirmLock === "unlock" ? "Unlock" : "Lock as final"}
        destructive={confirmLock === "unlock"}
        loading={saving}
        onConfirm={async () => {
          await onLock(activeSheet?.subjectPropertyId, confirmLock === "lock");
          setConfirmLock(null);
        }}
        onCancel={() => setConfirmLock(null)}
      />

      <RebuildSheetDialog
        open={confirmRebuild}
        lineCount={rows.length}
        busy={saving}
        onCancel={() => setConfirmRebuild(false)}
        onConfirm={handleRebuildConfirm}
      />

      {/* One line's photo evidence across every report, oldest first, so the
          line reads as progress over the life of the loan. */}
      {photoLineId && photoItems.length > 0 && api && (
        <ReportPhotoLightbox
          api={api}
          dealId={dealId}
          items={photoItems}
          index={Math.min(photoIdx, photoItems.length - 1)}
          onIndex={setPhotoIdx}
          onClose={() => setPhotoLineId(null)}
          onPreviewReport={onPreviewReport}
          title="this line, oldest first"
        />
      )}
    </Box>
  );
};

export default ScopeOfWorkWorkbook;
